import { readFile, writeFile } from 'fs/promises';
import { ImportResolver } from '../utils/import-resolver.js';
import { CompileConfig } from '../types/index.js';

/**
 * Convert MEMORY.md to AGENTS.md
 *
 * AGENTS.md doesn't natively support @import syntax.
 * Based on config, we either:
 * 1. Expand imports and append to end (default)
 * 2. Expand imports inline at declaration point
 *
 * @param sourcePath - Path to MEMORY.md
 * @param targetPath - Path to output AGENTS.md
 * @param config - Compilation configuration
 */
export async function memoryToAgents(
  sourcePath: string,
  targetPath: string,
  config: CompileConfig
): Promise<{ warnings: string[]; errors: string[] }> {
  try {
    const content = await readFile(sourcePath, 'utf-8');

    let outputContent: string;
    let warnings: string[] = [];
    let errors: string[] = [];

    if (config.expandImportsInAgents) {
      // Resolve imports
      const resolver = new ImportResolver({
        strictMode: config.strictMode,
      });

      const result = await resolver.resolveFile(sourcePath);
      outputContent = result.content;
      warnings = result.warnings;
      errors = result.errors;

      // In strict mode, throw if there are errors
      if (config.strictMode && errors.length > 0) {
        throw new Error(`Import resolution failed:\n${errors.join('\n')}`);
      }
    } else {
      // Don't expand imports, just copy content
      // (though AGENTS.md won't understand @import syntax)
      outputContent = content;
    }

    await writeFile(targetPath, outputContent, 'utf-8');

    return { warnings, errors };
  } catch (error) {
    throw new Error(
      `Failed to convert MEMORY.md to AGENTS.md: ${(error as Error).message}`
    );
  }
}
